import { csrfToken } from "@/lib/orderActions"
import { directUpload } from "@/lib/directUpload"

// Open House — the customer's brief: the project page status (phase-1-wording
// §5), the brief screens (§6), the accept-blocked dialog (§7) and editing a
// completed brief (new-flow-wording §12), and a bundle's cover brief
// (new-flow-wording §14–18, phase-1-wording §7). Every string is approved, verbatim.
//
// A brief is one of four kinds (OrderBriefs::Requirement::KINDS): "writing" and
// "sample" share the writer wording; "editing" and "cover" have their own. A
// bundle's two briefs name themselves on their edit buttons (new-flow-wording §30).

const WRITER = {
  status: "Brief still to finish",
  statusHelp: "Writers can still request your project. Finish the brief before accepting a writer’s request.",
  cta: "Finish brief",
  edit: "Edit brief",
  finish: "Finish brief",
  saving: "Saving brief…",
  saved: "Brief saved. Finish it before accepting a writer’s request.",
  saveFailed: "Your brief couldn’t be saved. Try again.",
  doneHeading: "Brief complete",
  doneBody: "You can now accept a writer’s request.",
  back: "Review writer requests",
  blockedHeading: "Finish your brief first",
  blockedBody: "Complete your brief before accepting this writer’s request.",
}

const EDITOR = {
  status: "Editing preferences still to finish",
  statusHelp: "Editors can still request your project. Review your preferences before accepting an editor’s request.",
  cta: "Review editing preferences",
  edit: "Review editing preferences",
  finish: "Finish review",
  saving: "Saving preferences…",
  saved: "Preferences saved. Finish your review before accepting an editor’s request.",
  saveFailed: "Your preferences couldn’t be saved. Try again.",
  doneHeading: "Editing preferences complete",
  doneBody: "You can now accept an editor’s request.",
  back: "Review editor requests",
  blockedHeading: "Review your editing preferences first",
  blockedBody: "Finish reviewing your preferences before accepting this editor’s request.",
}

export const BRIEF_COPY = {
  writing: {
    ...WRITER,
    heading: "Finish your writer brief",
    // new-flow-wording §30: beside a bundle's cover brief.
    editInBundle: "Edit writer brief",
    intro: "Review the remaining details your writer will use. You can keep your working topic and leave optional fields blank.",
  },
  sample: {
    ...WRITER,
    heading: "Finish your sample brief",
    intro: "Give your writer the remaining instructions for your 1,000-word sample. Your short description is already saved.",
  },
  editing: {
    ...EDITOR,
    heading: "Review your editing preferences",
    intro: "Your manuscript and project details are already saved. Review the remaining preferences before accepting an editor’s request.",
  },
  cover: {
    ...WRITER,
    heading: "Finish your cover brief",
    intro: "Review the remaining details your cover designer will use. You can leave optional fields blank.",
    status: "Cover brief still to finish",
    statusHelp: "Finish the cover brief before a cover designer can be assigned. You can still accept a writer’s request once your writer brief is complete.",
    cta: "Finish cover brief",
    // new-flow-wording §30.
    edit: "Edit cover brief",
    finish: "Finish cover brief",
    saving: "Saving cover brief…",
    saveFailed: "Cover brief not saved. Try again.",
    saved: "Cover brief saved. Finish it before a cover designer can be assigned.",
    doneHeading: "Cover brief complete",
    doneBody: "Your cover brief is ready for designer assignment.",
    back: "Back to project",
    blockedHeading: "Finish your cover brief first",
    blockedBody: "Complete your cover brief before accepting this cover designer’s request.",
    // new-flow-wording §16: shown where today's form asks the title, subtitle and author.
    fromBrief: "Title, subtitle and author name come from your writer brief.",
  },
}

export const briefCopy = (kind) => BRIEF_COPY[kind] ?? BRIEF_COPY.writing

export const BRIEF_WORDS = {
  saveLater: "Save and return later",
  summary: "Answer the required fields before finishing your brief.",
  backToRequests: "Back to requests",
  files: {
    empty: "No reference files added.",
    help: "Supported files: .doc, .docx, .pdf or .zip.",
    button: "Choose file",
    uploading: "Uploading file…",
    uploaded: "File uploaded.",
    failed: "Your file didn’t upload. Try again or remove it to continue.",
    unsupported: "Upload a .doc, .docx, .pdf or .zip file.",
    retry: "Retry upload",
    remove: "Remove file",
    // The cover brief's file rules are today's cover form's, with its refusal
    // (config/locales/en.yml order_briefs.errors.unsupported_cover_file).
    coverUnsupported: "Requirement file only accepts .doc, .docx, .pdf, .jpg, .jpeg, .gif, .png or .zip file types",
  },
}

// Each answer's label, help and placeholder (§6). `multiline` answers take a
// textarea; `toggle` answers are on/off; `choice` is a toggle's own line under
// its label. The sample brief's reference files have their own help.
export const BRIEF_FIELDS = {
  final_title: {
    label: "Final title (optional)",
    help: "Add a final title if you have one. Leave this blank to keep your title or working topic.",
    placeholder: "Your final title",
  },
  subtitle: { label: "Subtitle (optional)", help: "Add a subtitle if your book has one.", placeholder: "Your subtitle" },
  author_name: {
    label: "Author name",
    help: "Enter the name or pen name to use on the book.",
    placeholder: "Name as it should appear",
  },
  author_bio: {
    label: "Author bio (optional)",
    help: "Add a short bio if you want one included in the book.",
    placeholder: "A few lines about the author",
    multiline: true,
  },
  glossary: { label: "Add a glossary (optional)", help: "Include a list of terms and their meanings.", toggle: true },
  detailed_instructions: {
    label: "Detailed instructions (optional)",
    help: "Add any remaining instructions for your writer. Your description and outline are already saved.",
    placeholder: "Anything else your writer should follow",
    multiline: true,
  },
  reference_files: {
    label: "Reference files (optional)",
    help: "Add supporting material for your writer. You don’t need to upload your outline again.",
    sampleHelp: "Add supporting material your writer should use for the sample.",
  },
  sample_instructions: {
    label: "Sample instructions",
    help: "Explain what you want this sample to demonstrate and any specific instructions to follow.",
    placeholder: "e.g. Start with the discovery of the missing boat. Keep the dialogue brief and leave the scene unresolved.",
    multiline: true,
  },
  previous_editing: {
    label: "Previous editing (optional)",
    help: "Tell your editor whether you’ve self-edited the manuscript or had developmental editing done.",
    choice: "I’ve self-edited or had developmental editing done",
    toggle: true,
  },
  previous_editing_details: {
    label: "Previous editing details (optional)",
    help: "Add any useful details about that work.",
    placeholder: "What was reviewed or changed",
    multiline: true,
  },
  additional_instructions: {
    label: "Additional instructions (optional)",
    help: "Add any remaining preferences for your editor.",
    placeholder: "Anything else your editor should follow",
    multiline: true,
  },
}

// The keyed missing-answer errors (§6), by answer key; the cover brief's are
// today's cover form's (config/locales/en.yml order_briefs.missing), with
// new-flow-wording §19 for design samples.
export const MISSING = {
  author_name: "Enter the author name or pen name to use on the book.",
  sample_instructions: "Add instructions for your sample.",
  interior_type: "Interior Type is required",
  trim_size: "Trim Size is required",
  page_color: "Page Color is required",
  front_cover_options: "Front Cover Option is required",
  back_cover_options: "Back Cover Option is required",
  spine_cover_options: "Spine Cover Option is required",
  design_samples: "Choose at least 3 design samples.",
  color_palettes: "A minimum of 1 color is required",
  style: "A minimum of 2 style selections is required",
}

// Today's requirement file types (OrderBriefs::ReferenceFile).
export const REFERENCE_ACCEPT = ".doc,.docx,.pdf,.zip"

export const isReferenceFile = (file, accept = REFERENCE_ACCEPT) =>
  accept.split(",").some((ext) => String(file?.name || "").toLowerCase().endsWith(ext))

export const briefPagePath = (orderId) => `/projects/${orderId}/brief/edit`

// Saves the brief through PATCH /projects/:id/brief (OrderBriefsController):
// `answers` by key, `finish` to complete it, `removeFile` to remove the
// reference file. A File in answers.reference_files goes straight to storage
// first and is sent as its signed blob id. Resolves with { ok, brief, errors };
// a network failure rejects.
export async function saveBrief(orderId, { answers = {}, finish = false, removeFile = false } = {}) {
  const file = answers.reference_files
  const brief = typeof File !== "undefined" && file instanceof File
    ? { ...answers, reference_files: await directUpload(file) }
    : answers

  const res = await fetch(`/projects/${orderId}/brief`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ brief, finish, remove_reference_files: removeFile }),
  })
  const data = await res.json().catch(() => ({}))

  return { ok: res.ok, brief: data.brief ?? null, errors: data.errors ?? {} }
}
